import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { FiSearch, FiPlus, FiPackage, FiDollarSign, FiTrendingUp, FiShoppingBag, FiTrash2, FiX } from 'react-icons/fi';
import PurchaseTable from '../components/PurchaseTable';
import { Modal, Button, Form } from 'react-bootstrap';
import Select from 'react-select';

// Theme-aware styles for react-select dropdowns
const getSelectStyles = (isDark) => ({
  control: (base, state) => ({
    ...base,
    minHeight: '38px',
    height: '38px',
    backgroundColor: isDark ? '#2d2d2d' : '#fff',
    borderColor: state.isFocused ? (isDark ? '#4a9eff' : '#0d6efd') : (isDark ? '#444' : '#dee2e6'),
    boxShadow: state.isFocused ? `0 0 0 0.25rem ${isDark ? 'rgba(74, 158, 255, 0.25)' : 'rgba(13, 110, 253, 0.25)'}` : 'none',
    '&:hover': {
      borderColor: isDark ? '#4a9eff' : '#0d6efd'
    }
  }),
  valueContainer: (base) => ({
    ...base,
    height: '38px',
    padding: '0 8px'
  }),
  input: (base) => ({
    ...base,
    margin: '0',
    padding: '0',
    color: isDark ? '#fff' : '#000'
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '38px'
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: isDark ? '#2d2d2d' : '#fff',
    border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
    boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
  }),
  menuList: (base) => ({
    ...base,
    padding: '0'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected 
      ? (isDark ? '#0d6efd' : '#0d6efd')
      : state.isFocused 
        ? (isDark ? '#3a3a3a' : '#e9ecef')
        : 'transparent',
    color: state.isSelected ? '#fff' : (isDark ? '#fff' : '#000'),
    cursor: 'pointer',
    '&:active': {
      backgroundColor: isDark ? '#0d6efd' : '#0d6efd'
    }
  }),
  singleValue: (base) => ({
    ...base,
    color: isDark ? '#fff' : '#000'
  }),
  placeholder: (base) => ({
    ...base,
    color: isDark ? '#aaa' : '#6c757d'
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: isDark ? '#aaa' : '#6c757d',
    '&:hover': {
      color: isDark ? '#fff' : '#000'
    }
  }),
  clearIndicator: (base) => ({
    ...base,
    color: isDark ? '#aaa' : '#6c757d',
    '&:hover': {
      color: isDark ? '#fff' : '#000'
    }
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  })
});

export default function Purchases() {
  const { isDark } = useTheme();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [currentProduct, setCurrentProduct] = useState({
    productId: '',
    quantity: 1,
    totalCost: 0,
    purchaseType: 'units', // 'units' or 'packs'
    unitsPerPack: 1
  });
  const [categoryFilter, setCategoryFilter] = useState('');
  const [formData, setFormData] = useState({
    supplier: '',
    invoiceNumber: '',
    notes: '',
    status: 'completed',
    date: new Date().toISOString().split('T')[0]
  });
  const { selectedHierarchy } = useAuth();

  // Refs for form fields
  const supplierRef = useRef(null);
  const invoiceRef = useRef(null);
  const statusRef = useRef(null);
  const categoryRef = useRef(null);
  const productRef = useRef(null);
  const typeRef = useRef(null);
  const quantityRef = useRef(null);
  const totalCostRef = useRef(null);

  useEffect(() => {
    loadPurchases();
    loadProducts();
    loadCategories();
    loadStock();
  }, [selectedHierarchy]);

  // Focus on first field when modal opens
  useEffect(() => {
    if (showModal && supplierRef.current) {
      setTimeout(() => {
        supplierRef.current?.focus();
      }, 100);
    }
  }, [showModal]);

  const loadCategories = async () => {
    if (!selectedHierarchy?.outlet?.id) return;
    
    try {
      const categoriesRef = collection(db, 'categories');
      const q = query(
        categoriesRef,
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      const snapshot = await getDocs(q);
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadStock = async () => {
    if (!selectedHierarchy?.outlet?.id) return;
    
    try {
      const stockQuery1 = query(collection(db, 'stock'), where('outletId', '==', selectedHierarchy.outlet.id));
      const stockQuery2 = query(collection(db, 'stock'), where('outletId', '==', null));
      
      const [stockSnapshot1, stockSnapshot2] = await Promise.all([
        getDocs(stockQuery1),
        getDocs(stockQuery2)
      ]);
      
      const stockData = {};
      [...stockSnapshot1.docs, ...stockSnapshot2.docs].forEach(doc => {
        const data = doc.data();
        const productId = data.productId;
        
        if (!stockData[productId]) {
          stockData[productId] = 0;
        }
        stockData[productId] += (data.quantity || 0);
      });
      setStockMap(stockData);
    } catch (error) {
      console.error('Error loading stock:', error);
    }
  };

  const loadProducts = async () => {
    if (!selectedHierarchy?.outlet?.id) return;
    
    try {
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef,
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      const snapshot = await getDocs(q);
      const productsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Products loaded for purchases:', productsData.length);
      console.log('Sample product:', productsData[0]);
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadPurchases = async () => {
    if (!selectedHierarchy?.outlet?.id) {
      console.log('No outlet selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Loading purchases for outlet:', selectedHierarchy.outlet.id);
      const purchaseGroupsRef = collection(db, 'purchaseGroups');
      const q = query(
        purchaseGroupsRef,
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      
      const snapshot = await getDocs(q);
      console.log('Purchase groups found:', snapshot.size);
      const purchasesData = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Purchase group:', doc.id, data);
        
        // Map database fields to UI expected fields
        return {
          id: doc.id,
          ...data,
          supplierName: data.supplier || data.supplierName || 'Unknown',
          totalAmount: data.totalCost || data.totalAmount || 0,
          items: data.productCount || data.items || 0,
          date: data.createdAt?.toDate ? data.createdAt.toDate() : (data.date || new Date()),
          status: data.status || 'completed'
        };
      });
      
      console.log('Processed purchases data:', purchasesData);
      setPurchases(purchasesData);
    } catch (error) {
      console.error('Error loading purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) {
      alert('Please add at least one product to the purchase.');
      return;
    }

    try {
      const totalCost = selectedProducts.reduce((sum, item) => 
        sum + Number(item.totalCost), 0
      );
      
      const productCount = selectedProducts.reduce((sum, item) => 
        sum + Number(item.quantity), 0
      );

      const purchaseGroupData = {
        supplier: formData.supplier,
        invoiceNumber: formData.invoiceNumber,
        notes: formData.notes,
        status: formData.status,
        totalCost: totalCost,
        productCount: productCount,
        outletId: selectedHierarchy?.outlet?.id,
        createdAt: new Date()
      };

      // Create purchase group
      const groupRef = await addDoc(collection(db, 'purchaseGroups'), purchaseGroupData);

      // Create individual purchase items
      const purchasesPromises = selectedProducts.map(item => 
        addDoc(collection(db, 'purchases'), {
          purchaseGroupId: groupRef.id,
          productId: item.productId,
          productName: item.productName,
          quantity: Number(item.quantity),
          totalCost: Number(item.totalCost),
          costPerUnit: Number(item.costPerUnit),
          purchaseType: item.purchaseType,
          outletId: selectedHierarchy?.outlet?.id,
          createdAt: new Date()
        })
      );

      await Promise.all(purchasesPromises);

      setShowModal(false);
      resetForm();
      loadPurchases();
      alert('Purchase added successfully!');
    } catch (error) {
      console.error('Error saving purchase:', error);
      alert('Failed to save purchase. Please try again.');
    }
  };

  const addProductToList = () => {
    if (!categoryFilter) {
      alert('Please select a category');
      return;
    }
    
    if (!currentProduct.productId) {
      alert('Please select a product');
      return;
    }
    
    if (Number(currentProduct.quantity) <= 0) {
      alert('Please enter a valid quantity');
      return;
    }

    if (Number(currentProduct.totalCost) <= 0) {
      alert('Please enter a valid total cost');
      return;
    }

    const product = products.find(p => p.id === currentProduct.productId);
    if (!product) return;

    // Calculate total units based on purchase type
    const totalUnits = currentProduct.purchaseType === 'packs' 
      ? Number(currentProduct.quantity) * Number(currentProduct.unitsPerPack || 1)
      : Number(currentProduct.quantity);

    // Calculate cost per unit (actual individual unit, not per pack)
    const costPerUnit = Number(currentProduct.totalCost) / totalUnits;

    // Check if product already exists in list
    const existingIndex = selectedProducts.findIndex(item => item.productId === currentProduct.productId);
    
    if (existingIndex >= 0) {
      // Update existing product
      const updated = [...selectedProducts];
      const newQuantity = Number(updated[existingIndex].quantity) + Number(currentProduct.quantity);
      const newTotalCost = Number(updated[existingIndex].totalCost) + Number(currentProduct.totalCost);
      const newTotalUnits = Number(updated[existingIndex].totalUnits) + totalUnits;
      
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantity: newQuantity,
        totalCost: newTotalCost,
        totalUnits: newTotalUnits,
        purchaseType: currentProduct.purchaseType
      };
      // Recalculate cost per unit based on total units (not packs)
      updated[existingIndex].costPerUnit = newTotalCost / newTotalUnits;
      setSelectedProducts(updated);
    } else {
      // Add new product
      setSelectedProducts([...selectedProducts, {
        id: Date.now(),
        productId: currentProduct.productId,
        productName: product.name,
        productSku: product.sku || '',
        quantity: Number(currentProduct.quantity),
        totalUnits: totalUnits,
        totalCost: Number(currentProduct.totalCost),
        costPerUnit: costPerUnit,
        purchaseType: currentProduct.purchaseType,
        unitsPerPack: product.unitsPerPack || 1
      }]);
    }

    // Reset current product form
    setCurrentProduct({
      productId: '',
      quantity: 1,
      totalCost: 0,
      purchaseType: 'units',
      unitsPerPack: 1
    });

    // Focus back to category select to add next product
    setTimeout(() => {
      categoryRef.current?.focus();
    }, 100);
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setCurrentProduct({
        productId: productId,
        quantity: 1,
        totalCost: product.cost || 0,
        purchaseType: 'units',
        unitsPerPack: product.unitsPerPack || 1
      });
    }
  };

  const calculateCostPerUnit = () => {
    if (currentProduct.quantity > 0 && currentProduct.totalCost > 0) {
      return (Number(currentProduct.totalCost) / Number(currentProduct.quantity)).toFixed(2);
    }
    return '0.00';
  };

  const calculateActualCostPerUnit = () => {
    if (currentProduct.totalCost > 0) {
      const totalUnits = calculateTotalUnits();
      if (totalUnits > 0) {
        return (Number(currentProduct.totalCost) / totalUnits).toFixed(2);
      }
    }
    return '0.00';
  };

  const calculateTotalUnits = () => {
    if (currentProduct.purchaseType === 'packs') {
      return Number(currentProduct.quantity) * Number(currentProduct.unitsPerPack || 1);
    }
    return Number(currentProduct.quantity);
  };

  const handleFormKeyPress = (e, nextRef) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation(); // Prevent form submission
      if (nextRef && nextRef.current) {
        // For react-select components, we need to focus the input element
        if (nextRef.current.focus) {
          nextRef.current.focus();
        } else if (nextRef.current.select) {
          nextRef.current.select.focus();
        }
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation(); // Prevent form submission
      addProductToList();
    }
  };

  const removeProductFromList = (id) => {
    setSelectedProducts(selectedProducts.filter(item => item.id !== id));
  };

  const updateProductInList = (id, field, value) => {
    setSelectedProducts(selectedProducts.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        
        // Recalculate total units and cost per unit when quantity or total cost changes
        if (field === 'quantity' || field === 'totalCost') {
          // Recalculate total units based on purchase type
          if (item.purchaseType === 'packs') {
            updated.totalUnits = Number(updated.quantity) * Number(item.unitsPerPack || 1);
          } else {
            updated.totalUnits = Number(updated.quantity);
          }
          
          // Recalculate cost per unit based on total units (not packs)
          updated.costPerUnit = Number(updated.totalCost) / Number(updated.totalUnits);
        }
        
        return updated;
      }
      return item;
    }));
  };

  const handleDelete = async (purchase) => {
    // SECURITY: Verify the purchase belongs to the current outlet
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    if (purchase.outletId !== selectedHierarchy.outlet.id) {
      alert('Security Error: Cannot delete purchase from another outlet.');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete this purchase from ${purchase.supplierName}?`)) {
      try {
        await deleteDoc(doc(db, 'purchaseGroups', purchase.id));
        loadPurchases();
      } catch (error) {
        console.error('Error deleting purchase:', error);
        alert('Failed to delete purchase.');
      }
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const handleOpenDetailsDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setShowDetailsModal(true);
  };

  const handleOpenDeleteDialog = (purchase) => {
    handleDelete(purchase);
  };

  const resetForm = () => {
    setFormData({
      supplier: '',
      invoiceNumber: '',
      notes: '',
      status: 'completed',
      date: new Date().toISOString().split('T')[0]
    });
    setSelectedProducts([]);
    setCurrentProduct({
      productId: '',
      quantity: 1,
      totalCost: 0,
      purchaseType: 'units'
    });
    setCategoryFilter('');
  };

  const filteredPurchases = purchases.filter(purchase => {
    // Search filter
    const matchesSearch = purchase.supplierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         purchase.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         purchase.status?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Status filter
    if (statusFilter !== 'all' && purchase.status !== statusFilter) return false;
    
    // Date filter
    if (dateFilter !== 'all') {
      const purchaseDate = new Date(purchase.createdAt?.seconds ? purchase.createdAt.seconds * 1000 : purchase.createdAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      switch (dateFilter) {
        case 'today':
          const startOfToday = new Date(today);
          if (purchaseDate < startOfToday) return false;
          break;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          if (purchaseDate < weekAgo) return false;
          break;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          if (purchaseDate < monthAgo) return false;
          break;
        case 'year':
          const yearAgo = new Date(today);
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          if (purchaseDate < yearAgo) return false;
          break;
        default:
          break;
      }
    }
    
    return true;
  });

  const sortedPurchases = [...filteredPurchases].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    if (sortBy === 'date') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    } else if (sortBy === 'totalAmount') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const totalPurchaseAmount = purchases.reduce((sum, p) => sum + (Number(p.totalAmount) || 0), 0);
  const totalItems = purchases.reduce((sum, p) => sum + (Number(p.items) || 0), 0);

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h1 className="page-title">Purchases</h1>
        <p className="page-subtitle">Manage inventory purchases and suppliers</p>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Total Purchases</p>
                  <h3 className="mb-0">{purchases.length}</h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--primary-gradient)', width: '48px', height: '48px' }}>
                  <FiShoppingBag className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Total Amount</p>
                  <h3 className="mb-0">{totalPurchaseAmount.toFixed(2)}</h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--success-gradient)', width: '48px', height: '48px' }}>
                  <FiDollarSign className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Total Items</p>
                  <h3 className="mb-0">{totalItems}</h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--accent-gradient)', width: '48px', height: '48px' }}>
                  <FiPackage className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Pending</p>
                  <h3 className="mb-0 text-warning">
                    {purchases.filter(p => p.status === 'pending').length}
                  </h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--warning-gradient)', width: '48px', height: '48px' }}>
                  <FiTrendingUp className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Purchases Table */}
      <div className="card hover-lift">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Purchase Orders</h5>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
            <FiPlus className="me-2" />
            New Purchase
          </button>
        </div>

        {/* Filter Controls */}
        {!loading && purchases.length > 0 && (
          <div className="card-body border-bottom">
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label small text-muted mb-1">Search</label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text"><FiSearch /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by supplier, invoice..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Status</label>
                <select 
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Date Range</label>
                <select 
                  className="form-select form-select-sm"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="year">This Year</option>
                </select>
              </div>
              <div className="col-md-2">
                <button
                  className="btn btn-sm btn-outline-secondary w-100"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setDateFilter('all');
                  }}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Clear
                </button>
              </div>
            </div>
            {(searchTerm || statusFilter !== 'all' || dateFilter !== 'all') && (
              <div className="mt-2">
                <small className="text-muted">
                  Showing {sortedPurchases.length} of {purchases.length} purchases
                </small>
              </div>
            )}
          </div>
        )}

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading purchases...</p>
            </div>
          ) : sortedPurchases.length === 0 ? (
            <div className="text-center py-5">
              <FiShoppingBag size={48} className="text-muted mb-3" />
              <p className="text-muted mb-0">
                {searchTerm || statusFilter !== 'all' || dateFilter !== 'all'
                  ? 'No purchases match your filters.'
                  : !selectedHierarchy?.outlet?.id 
                    ? 'Please select a business outlet to view purchases'
                    : 'No purchases found.'}
              </p>
              {(searchTerm || statusFilter !== 'all' || dateFilter !== 'all') && (
                <button
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setDateFilter('all');
                  }}
                >
                  Clear Filters
                </button>
              )}
              {!searchTerm && statusFilter === 'all' && dateFilter === 'all' && selectedHierarchy?.outlet?.id && (
                <button className="btn btn-primary mt-3" onClick={() => setShowModal(true)}>
                  <FiPlus className="me-2" />
                  Add First Purchase
                </button>
              )}
            </div>
          ) : (
            <PurchaseTable
              paginatedPurchaseGroups={sortedPurchases}
              sortBy={sortBy}
              sortDirection={sortDirection}
              handleSort={handleSort}
              handleOpenDetailsDialog={handleOpenDetailsDialog}
              handleOpenDeleteDialog={handleOpenDeleteDialog}
            />
          )}
        </div>
      </div>

      {/* Add Purchase Modal */}
      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>New Purchase</Modal.Title>
        </Modal.Header>
        <div>
          <Modal.Body>
            {/* Purchase Details */}
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Supplier Name *</Form.Label>
                  <Form.Control
                    ref={supplierRef}
                    type="text"
                    value={formData.supplier}
                    onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                    onKeyPress={(e) => handleFormKeyPress(e, invoiceRef)}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Invoice Number *</Form.Label>
                  <Form.Control
                    ref={invoiceRef}
                    type="text"
                    value={formData.invoiceNumber}
                    onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                    onKeyPress={(e) => handleFormKeyPress(e, statusRef)}
                    required
                  />
                </Form.Group>
              </div>
                  <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Select
                    ref={statusRef}
                    options={[
                      { value: 'completed', label: 'Completed' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'cancelled', label: 'Cancelled' }
                    ]}
                    value={{ value: formData.status, label: formData.status.charAt(0).toUpperCase() + formData.status.slice(1) }}
                    onChange={(option) => {
                      setFormData({ ...formData, status: option.value });
                      // Move to next field after selection
                      setTimeout(() => categoryRef.current?.focus(), 100);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        // Check if menu is closed
                        const menuIsOpen = e.target.closest('.select__control')?.classList.contains('select__control--menu-is-open');
                        if (!menuIsOpen) {
                          // Menu is closed, move to next field (Status always has a value)
                          setTimeout(() => categoryRef.current?.focus(), 50);
                        }
                        // If menu is open, let react-select handle the selection naturally
                      }
                    }}
                    menuPortalTarget={document.body}
                    styles={getSelectStyles(isDark)}
                  />
                </Form.Group>
              </div>
            </div>

            {/* Add Product Form - Always Visible */}
            <div className="card mb-3 border-primary">
              <div className="card-header bg-primary text-white">
                <h6 className="mb-0">Add Product to Purchase</h6>
              </div>
              <div className="card-body">
                {/* All fields in one row */}
                <div className="row g-2">
                  <div className="col-12 col-md-6 col-lg-4 col-xl-3">
                    <Form.Label className="small">Category *</Form.Label>
                    <Select
                      ref={categoryRef}
                      placeholder="Select Category"
                      options={categories.map(cat => ({ value: cat.id, label: cat.name }))}
                      value={categoryFilter ? { value: categoryFilter, label: categories.find(c => c.id === categoryFilter)?.name } : null}
                      onChange={(option) => {
                        setCategoryFilter(option?.value || '');
                        // Move to next field after selection
                        setTimeout(() => productRef.current?.focus(), 100);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          // Check if menu is open
                          const menuIsOpen = e.target.closest('.select__control')?.classList.contains('select__control--menu-is-open');
                          if (!menuIsOpen) {
                            // Menu is closed - check if there's a value selected
                            if (categoryFilter) {
                              // Has value, move to next field
                              setTimeout(() => productRef.current?.focus(), 50);
                            }
                            // If no value, Enter will open the menu (react-select default behavior)
                          }
                          // If menu is open, let react-select handle the selection naturally
                        }
                      }}
                      menuPortalTarget={document.body}
                      styles={getSelectStyles(isDark)}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4 col-xl-3">
                    <Form.Label className="small">Product * ({products.filter(p => !categoryFilter || p.categoryId === categoryFilter).length})</Form.Label>
                    <Select
                      ref={productRef}
                      id="productSelect"
                      placeholder="Select..."
                      options={products
                        .filter(p => !categoryFilter || p.categoryId === categoryFilter)
                        .map(product => ({
                          value: product.id,
                          label: `${product.name} ${product.sku ? `(${product.sku})` : ''}`
                        }))}
                      value={currentProduct.productId ? {
                        value: currentProduct.productId,
                        label: products.find(p => p.id === currentProduct.productId)?.name
                      } : null}
                      onChange={(option) => {
                        handleProductSelect(option?.value || '');
                        // Move to next field after selection
                        setTimeout(() => typeRef.current?.focus(), 100);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          // Check if menu is open
                          const menuIsOpen = e.target.closest('.select__control')?.classList.contains('select__control--menu-is-open');
                          if (!menuIsOpen) {
                            // Menu is closed - check if there's a value selected
                            if (currentProduct.productId) {
                              // Has value, move to next field
                              setTimeout(() => typeRef.current?.focus(), 50);
                            }
                            // If no value, Enter will open the menu (react-select default behavior)
                          }
                          // If menu is open, let react-select handle the selection naturally
                        }
                      }}
                      menuPortalTarget={document.body}
                      styles={getSelectStyles(isDark)}
                    />
                  </div>
                  <div className="col-12 col-md-4 col-lg-2 col-xl">
                    <Form.Label className="small">Type *</Form.Label>
                    <Select
                      ref={typeRef}
                      options={[
                        { value: 'units', label: 'Units' },
                        { value: 'packs', label: 'Packs' }
                      ]}
                      value={{ value: currentProduct.purchaseType, label: currentProduct.purchaseType === 'units' ? 'Units' : 'Packs' }}
                      onChange={(option) => {
                        setCurrentProduct({ ...currentProduct, purchaseType: option.value });
                        // Move to next field after selection
                        setTimeout(() => quantityRef.current?.focus(), 100);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          // Check if menu is open
                          const menuIsOpen = e.target.closest('.select__control')?.classList.contains('select__control--menu-is-open');
                          if (!menuIsOpen) {
                            // Menu is closed - Type always has a value (defaults to 'units')
                            // Move to next field
                            setTimeout(() => quantityRef.current?.focus(), 50);
                          }
                          // If menu is open, let react-select handle the selection naturally
                        }
                      }}
                      menuPortalTarget={document.body}
                      styles={getSelectStyles(isDark)}
                    />
                  </div>
                  <div className="col-12 col-md-4 col-lg-2 col-xl">
                    <Form.Label className="small">
                      {currentProduct.purchaseType === 'packs' ? 'No. of Packs *' : 'Quantity *'}
                    </Form.Label>
                    <Form.Control
                      ref={quantityRef}
                      type="number"
                      min="1"
                      value={currentProduct.quantity}
                      onChange={(e) => setCurrentProduct({ ...currentProduct, quantity: e.target.value })}
                      onKeyPress={(e) => handleFormKeyPress(e, totalCostRef)}
                    />
                  </div>
                  <div className="col-12 col-md-4 col-lg-2 col-xl">
                    <Form.Label className="small">Total Cost *</Form.Label>
                    <Form.Control
                      ref={totalCostRef}
                      type="number"
                      step="0.01"
                      min="0"
                      value={currentProduct.totalCost}
                      onChange={(e) => setCurrentProduct({ ...currentProduct, totalCost: e.target.value })}
                      onKeyPress={handleKeyPress}
                    />
                  </div>
                  <div className="col-12 col-md-4 col-lg-2 col-xl-auto">
                    <Form.Label className="small d-block">&nbsp;</Form.Label>
                    <Button 
                      variant="primary"
                      onClick={addProductToList}
                      type="button"
                      className="w-100"
                    >
                      <FiPlus />
                    </Button>
                  </div>
                </div>
                
                {/* Product Information Display */}
                {currentProduct.productId && (() => {
                  const product = products.find(p => p.id === currentProduct.productId);
                  const currentStock = stockMap[currentProduct.productId] || 0;
                  const newStock = currentStock + calculateTotalUnits();
                  
                  return (
                    <div className="mt-2 p-2 bg-info bg-opacity-10 rounded border border-info">
                      <div className="row text-center g-2">
                        <div className="col">
                          <small className="text-muted d-block">Units per Pack</small>
                          <strong className="text-dark">{currentProduct.unitsPerPack || 1}</strong>
                        </div>
                        <div className="col">
                          <small className="text-muted d-block">Total Units</small>
                          <strong className="text-info">{calculateTotalUnits()}</strong>
                        </div>
                        {currentProduct.purchaseType === 'packs' && (
                          <div className="col">
                            <small className="text-muted d-block">Cost per Pack</small>
                            <strong className="text-primary">{calculateCostPerUnit()}</strong>
                          </div>
                        )}
                        <div className="col">
                          <small className="text-muted d-block">Cost per Unit</small>
                          <strong className="text-primary">{calculateActualCostPerUnit()}</strong>
                        </div>
                        <div className="col">
                          <small className="text-muted d-block">Current Stock</small>
                          <strong className="text-warning">{currentStock}</strong>
                        </div>
                        <div className="col">
                          <small className="text-muted d-block">New Stock</small>
                          <strong className="text-success">{newStock}</strong>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Added Products Table */}
            {selectedProducts.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0">Added Products ({selectedProducts.length})</h6>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '25%' }}>Product</th>
                          <th style={{ width: '10%' }} className="text-center">Type</th>
                          <th style={{ width: '12%' }} className="text-center">Quantity</th>
                          <th style={{ width: '12%' }} className="text-center">Total Units</th>
                          <th style={{ width: '15%' }} className="text-end">Total Cost</th>
                          <th style={{ width: '15%' }} className="text-end">Cost/Unit</th>
                          <th style={{ width: '8%' }} className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedProducts.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <div>
                                <strong>{item.productName}</strong>
                                {item.productSku && (
                                  <div className="text-muted small">SKU: {item.productSku}</div>
                                )}
                              </div>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-secondary">
                                {item.purchaseType === 'packs' ? 'Packs' : 'Units'}
                              </span>
                            </td>
                            <td className="text-center">
                              <Form.Control
                                size="sm"
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateProductInList(item.id, 'quantity', e.target.value)}
                                style={{ width: '80px', margin: '0 auto' }}
                              />
                              {item.purchaseType === 'packs' && item.unitsPerPack > 1 && (
                                <small className="text-muted d-block">({item.unitsPerPack}/pack)</small>
                              )}
                            </td>
                            <td className="text-center align-middle">
                              <strong className="text-info">
                                {item.purchaseType === 'packs' 
                                  ? item.quantity * (item.unitsPerPack || 1)
                                  : item.quantity
                                }
                              </strong>
                            </td>
                            <td className="text-end">
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.totalCost}
                                onChange={(e) => updateProductInList(item.id, 'totalCost', e.target.value)}
                                style={{ width: '120px', marginLeft: 'auto' }}
                              />
                            </td>
                            <td className="text-end align-middle">
                              <span className="text-primary">
                                {Number(item.costPerUnit).toFixed(2)}
                              </span>
                            </td>
                            <td className="text-center align-middle">
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => removeProductFromList(item.id)}
                                title="Remove"
                              >
                                <FiTrash2 />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot style={{ 
                        backgroundColor: isDark ? '#2d2d2d' : '#f8f9fa',
                        borderTop: `2px solid ${isDark ? '#444' : '#dee2e6'}`
                      }}>
                        <tr>
                          <td colSpan="2" className="text-end" style={{ 
                            color: isDark ? '#fff' : '#000',
                            fontWeight: 'bold'
                          }}>
                            Totals:
                          </td>
                          <td className="text-center" style={{ 
                            color: isDark ? '#fff' : '#000',
                            fontWeight: 'bold'
                          }}>
                            {selectedProducts.reduce((sum, item) => sum + Number(item.quantity), 0)}
                          </td>
                          <td colSpan="1"></td>
                          <td colSpan="2" className="text-end">
                            <strong className="text-success fs-5">
                              {selectedProducts.reduce((sum, item) => 
                                sum + Number(item.totalCost), 0
                              ).toFixed(2)}
                            </strong>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {selectedProducts.length === 0 && (
              <div className="alert alert-info mb-0">
                <FiPackage className="me-2" />
                No products added yet. Use the form above to add products to this purchase.
              </div>
            )}

            {/* Notes Section */}
            {selectedProducts.length > 0 && (
              <div className="mt-3">
                <Form.Group>
                  <Form.Label>Notes (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes about this purchase..."
                  />
                </Form.Group>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={selectedProducts.length === 0}>
              <FiShoppingBag className="me-2" />
              Save Purchase ({selectedProducts.length} {selectedProducts.length === 1 ? 'item' : 'items'})
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Purchase Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPurchase && (
            <div className="row g-3">
              <div className="col-md-6">
                <strong>Supplier:</strong> {selectedPurchase.supplierName}
              </div>
              <div className="col-md-6">
                <strong>Date:</strong> {new Date(selectedPurchase.date).toLocaleDateString()}
              </div>
              <div className="col-md-6">
                <strong>Total Amount:</strong> {Number(selectedPurchase.totalAmount).toFixed(2)}
              </div>
              <div className="col-md-6">
                <strong>Items:</strong> {selectedPurchase.items || 0}
              </div>
              <div className="col-12">
                <strong>Status:</strong> <span className={`badge bg-${selectedPurchase.status === 'completed' ? 'success' : 'warning'}`}>
                  {selectedPurchase.status}
                </span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
